import { useEffect, useMemo, useRef, useState } from 'react';

import { Search } from 'icons';
import { ActionIcon, ActionIconSize, ActionIconTheme, TextInput } from 'ui/atoms';

import { ThresholdConfig, getColor, getSeverity } from '../../MapSharedData';
import { Aggregated } from '../types';

interface Props {
  countryName: string;
  totalIncidents: number;
  totalPostcodes: number;
  unmatchedCount: number;
  aggregated: Aggregated;
  cfg: ThresholdConfig;
  onSelect: (regionName: string) => void;
  /** Triggered by the ✕ in the header — typically navigates back to world view. */
  onClose: () => void;
}

/**
 * Sidebar listing all regions in the active country that have incidents,
 * sorted by total descending. Clicking a region triggers drill-down (same
 * effect as clicking a region polygon on the map). Replaced by PostcodeSidebar
 * once a region is selected.
 *
 * Includes a search box to filter regions by name — handy for countries with
 * many regions (US has 50 states, Poland 16 voivodeships).
 *
 * Rendered as a flush right-side panel matching PostcodeSidebar's style so
 * the layout stays consistent at every drill-down level.
 */
export function RegionsSidebar({
  countryName,
  totalIncidents,
  totalPostcodes,
  unmatchedCount,
  aggregated,
  cfg,
  onSelect,
  onClose,
}: Props) {
  const [query, setQuery] = useState('');
  // -1 = nothing highlighted (typing freely). 0+ = a row is highlighted for
  // keyboard selection. Driven by ArrowUp/ArrowDown in the search input.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Autofocus the search input when this sidebar mounts (i.e. each time the
  // user lands on the country/region level) so they can start typing right
  // away without clicking. We query the inner <input> since TextInput is a
  // wrapper component without a forwarded ref.
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    searchWrapRef.current?.querySelector('input')?.focus();
  }, []);

  const allRows = useMemo(() => {
    return Object.entries(aggregated)
      .filter(([, data]) => data.total > 0)
      .map(([name, data]) => {
        const maxInRegion = data.postcodes.reduce((m, p) => Math.max(m, p.count), 0);
        return {
          name,
          total: data.total,
          max: maxInRegion,
          postcodes: data.postcodes.length,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [aggregated]);

  // Filter by case-insensitive substring match on the region name
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => r.name.toLowerCase().includes(q));
  }, [allRows, query]);

  // Reset highlight whenever the filtered set changes (new query) so we don't
  // point at a stale index.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  // Keep the highlighted row scrolled into view as the user arrows through.
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-row-index="${highlightedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Keyboard navigation following the ARIA combobox pattern:
  //   ArrowDown / ArrowUp — move highlight (wraps at the ends)
  //   Enter               — select the highlighted row
  //   Escape              — clear the query (or blur if already empty)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (rows.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const row = rows[idx];
      if (row) onSelect(row.name);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // don't let the global Escape handler also fire
      if (query) {
        setQuery('');
      } else {
        (e.target as HTMLInputElement).blur();
      }
    }
  };

  return (
    <div className="w-[280px] bg-white border-l border-v2blueGray-200 flex flex-col overflow-hidden shrink-0">
      <div className="px-4 py-3.5 border-b border-v2blueGray-200">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="text-[11px] text-v2blueGray-500 uppercase tracking-[0.06em]">
            {countryName}
          </div>
          <ActionIcon
            size={ActionIconSize.xs}
            theme={ActionIconTheme.gray200}
            onClick={onClose}
            title="Back to world"
            className="text-base"
          >
            ✕
          </ActionIcon>
        </div>
        <div className="text-base font-semibold text-v2blueGray-900 leading-tight">
          {totalIncidents.toLocaleString()} incidents
        </div>
        <div className="text-[11px] text-v2blueGray-500 mt-0.5">
          {totalPostcodes} postcode{totalPostcodes !== 1 ? 's' : ''} · {allRows.length} region
          {allRows.length !== 1 ? 's' : ''}
        </div>
        {unmatchedCount > 0 && (
          <div className="text-[10px] text-v2blueGray-600 mt-0.5">
            {unmatchedCount} postcode{unmatchedCount > 1 ? 's' : ''} not matched
          </div>
        )}
      </div>

      {/* Search — filters the region list by name */}
      <div ref={searchWrapRef} className="px-3 py-2 border-b border-v2blueGray-200">
        <TextInput
          name="region-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search regions…"
          icon={<Search />}
          iconPosition="left"
        />
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-v2blueGray-500 text-[13px]">
            {query ? `No regions matching "${query}"` : 'No regions with incidents'}
          </div>
        ) : (
          rows.map((row, index) => {
            const color = getColor(row.max, cfg);
            const sev = getSeverity(row.max, cfg);
            const isHighlighted = index === highlightedIndex;
            return (
              <button
                key={row.name}
                data-row-index={index}
                onClick={() => onSelect(row.name)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`flex items-center gap-2.5 w-full px-4 py-2.5 border-none border-b border-v2blueGray-200 cursor-pointer text-left transition-colors duration-150 ${
                  isHighlighted ? 'bg-v2blueGray-100' : 'bg-transparent hover:bg-v2blueGray-100'
                }`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full border-[1.5px] border-white shrink-0"
                  style={{ background: color, boxShadow: '0 0 0 1px rgba(31,60,78,0.1)' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium text-v2blueGray-900 truncate">
                      {row.name}
                    </span>
                    <span className="text-[13px] font-semibold" style={{ color: sev.color }}>
                      {row.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="text-[11px] text-v2blueGray-600">
                    {row.postcodes} postcode{row.postcodes !== 1 ? 's' : ''} · max{' '}
                    {row.max.toLocaleString()}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="px-4 py-2 border-t border-v2blueGray-200 text-[11px] text-v2blueGray-500 italic">
        Click a region or use ↑↓ Enter
      </div>
    </div>
  );
}
